/**
 * A single "will this run on the target device" score, 0-100.
 *
 * Pure, so the number in the panel, the number the AI reports and the number a
 * test asserts are the same number. It aggregates the per-metric judgments in
 * {@link ./performanceRatings} — it does NOT invent new thresholds.
 *
 * Two rules keep the score honest:
 *
 * 1. **Unmeasured never costs points.** A category with no data drops out and its
 *    weight is redistributed. Scoring "no GPU timing yet" as zero would tell users
 *    to fix something we simply have not looked at.
 * 2. **Every lost point is attributable.** The issues add up exactly to
 *    `100 - score`, so the panel can say what to fix and the sum always explains
 *    the headline. A score you cannot explain is a score nobody can act on.
 */

import { toHumanReadableBytes } from "../../../../base/common/humanize.js";
import {
    rateFrameStability, rateFps, rateTriangles, rateVram,
    type DeviceProfile, type MetricRating,
} from "./performanceRatings";

export type ScoreCategoryId = "frames" | "gpu" | "memory" | "load";

export interface ScoreInput {
    /** the TARGET device's budgets (auto = this device, or a Pro preset) */
    readonly profile: DeviceProfile;
    /**
     * Is the target THIS machine (the "Auto" target)? When it is not, the measured
     * fps is evidence about the dev machine and says nothing about the phone being
     * targeted — so the frames category drops out and its weight goes to the
     * budget-based ones. Scoring a Quest build 100 because the desktop renders it
     * at 120 fps would be a lie in the one place the score is supposed to help.
     */
    readonly targetIsThisDevice?: boolean;
    /** display cadence the fps is judged against */
    readonly targetFps?: number;
    readonly fps?: number;
    /** per-second fps history — sustained dips, not one janky frame */
    readonly fpsSamples?: readonly number[];
    readonly gpuMemoryBytes?: number;
    /** VRAM that compression/dedupe could reclaim — waste, not use */
    readonly gpuMemoryWasteBytes?: number;
    readonly trianglesPerFrame?: number;
    readonly drawCalls?: number;
    /** first uncached load estimate */
    readonly transferredBytes?: number;
}

export interface PerformanceScoreSnapshot {
    readonly deviceProfile?: DeviceProfile;
    readonly effectiveProfile?: DeviceProfile;
    readonly targetProfile?: DeviceProfile | null;
    readonly targetFps?: number;
    readonly fps?: number;
    readonly history?: readonly { readonly fps: number }[];
    readonly gpuMemory?: number;
    readonly gpuMemorySavings?: {
        readonly compress: number;
        readonly duplicate: number;
        readonly geometry?: number;
        readonly hdr?: number;
    };
    readonly renderStats?: { readonly triangles: number; readonly drawCalls: number };
    readonly totalResourceDownload?: number;
}

export interface ScoreCategory {
    readonly id: ScoreCategoryId;
    /**
     * The MEASUREMENTS behind this category's number, in plain words.
     *
     * A tooltip that repeats what "Download" means does not tell you why THIS
     * scene scored 45; "11.0 MB, budget 10.0 MB for this device" does. Built here
     * because this is where the inputs and the thresholds meet — the UI would have
     * to re-derive both to say the same thing, and would drift.
     */
    readonly facts?: readonly string[];
    readonly label: string;
    /** 0-100 for this category; undefined when nothing in it was measured */
    readonly score?: number;
    readonly rating?: MetricRating;
    /** false when no input for this category existed (weight was redistributed) */
    readonly measured: boolean;
    readonly weight: number;
}

export interface ScoreIssue {
    readonly category: ScoreCategoryId;
    /** short, imperative, human — shown as "biggest win" */
    readonly label: string;
    /** how many points of the final 100 this cost, rounded, worst first */
    readonly costPoints: number;
}

export interface PerformanceScore {
    /** undefined = nothing measured yet (show a spinner, not a zero) */
    readonly score?: number;
    readonly rating?: MetricRating;
    readonly categories: readonly ScoreCategory[];
    /** worst first; costs sum to 100 - score */
    readonly issues: readonly ScoreIssue[];
}

/** Category weights. Frames dominate on purpose: it is the thing users FEEL. */
const WEIGHTS: Record<ScoreCategoryId, number> = { frames: 0.4, gpu: 0.3, memory: 0.15, load: 0.15 };

const LABELS: Record<ScoreCategoryId, string> = {
    frames: "Frames", gpu: "GPU", memory: "Memory", load: "Download",
};

/** One line per category, for the UI tooltip and the AI report — the label alone
 *  does not say WHICH numbers went into it. */
export const SCORE_CATEGORY_HINTS: Record<ScoreCategoryId, string> = {
    frames: "How close the frame rate stays to your display's refresh rate — and how often it dips below it. Only scored when the target is this device.",
    gpu: "Per-frame GPU work: triangles rasterized and draw calls, plus VRAM against the target device's budget.",
    memory: "How much of the VRAM in use could be reclaimed — compressing textures, deduping identical ones, shipping FastHDR.",
    load: "First uncached download: the bytes a new visitor pulls before the scene is there. Judged harder on mobile targets, where the connection usually is too.",
};

/** A rating as a category score. Deliberately coarse — it mirrors the raters. */
const RATING_SCORE: Record<MetricRating, number> = { good: 100, warn: 60, bad: 25 };

function ratingFor(score: number): MetricRating {
    if (score >= 90) return "good";
    if (score >= 50) return "warn";
    return "bad";
}

/** Ratio against a budget as a 0-100 sub-score: at/under 70% of budget is full
 *  marks (the raters' "good" line), at the budget it is 50, over it decays. */
function budgetScore(used: number, budget: number | undefined): number | undefined {
    if (!budget || budget <= 0 || !Number.isFinite(used)) return undefined;
    const ratio = used / budget;
    if (ratio <= 0.7) return 100;
    if (ratio <= 1) return Math.round(100 - ((ratio - 0.7) / 0.3) * 50); // 100 → 50
    return Math.max(0, Math.round(50 / ratio)); // 2x over budget → 25, 4x → 12
}

/** bytes a person reads without arithmetic — the same helper the report uses */
function bytes(value: number): string {
    return toHumanReadableBytes(value, value < 1024 * 1024 * 1024 ? 1 : 2);
}

/** thousands separators, so 1500000 does not have to be counted by eye */
function formatCount(value: number): string {
    return Math.round(value).toLocaleString("en-US");
}

/** a share that exists must not print as "0%" — sub-1% keeps a decimal */
function sharePercent(fraction: number): string {
    const pct = fraction * 100;
    if (pct >= 1) return `${Math.round(pct)}%`;
    if (pct >= 0.05) return `${pct.toFixed(1)}%`;
    return "<0.1%";
}

/** mean of the defined values, or undefined when none are */
function mean(values: (number | undefined)[]): number | undefined {
    const defined = values.filter((v): v is number => v !== undefined);
    if (defined.length === 0) return undefined;
    return defined.reduce((a, b) => a + b, 0) / defined.length;
}

/**
 * One word for a score. Lives HERE, not in the card, because the report printed
 * the internal rating ("good") next to the same number the card called "Perfect"
 * — two vocabularies for one value. Perfect is reserved for an actual 100: a
 * scale nobody can top is not a scale.
 */
export function scoreVerdict(score: number): string {
    if (score >= 100) return "Perfect";
    if (score >= 90) return "Excellent";
    if (score >= 75) return "Good";
    if (score >= 60) return "Fair";
    if (score >= 40) return "Needs work";
    return "Poor";
}

/** Builds the shared score from a live or frozen performance-service snapshot. */
export function scorePerformanceSnapshot(
    snapshot: PerformanceScoreSnapshot,
    targetIsThisDevice = snapshot.targetProfile == null,
): PerformanceScore {
    const savings = snapshot.gpuMemorySavings;
    return scorePerformance({
        profile: snapshot.effectiveProfile ?? snapshot.deviceProfile ?? {},
        targetIsThisDevice,
        targetFps: snapshot.targetFps,
        fps: snapshot.fps,
        fpsSamples: snapshot.history?.map(sample => sample.fps),
        gpuMemoryBytes: snapshot.gpuMemory,
        gpuMemoryWasteBytes: savings
            ? savings.compress + savings.duplicate + (savings.geometry ?? 0) + (savings.hdr ?? 0)
            : undefined,
        trianglesPerFrame: snapshot.renderStats?.triangles,
        drawCalls: snapshot.renderStats?.drawCalls,
        transferredBytes: snapshot.totalResourceDownload,
    });
}

export function scorePerformance(input: ScoreInput): PerformanceScore {
    const { profile } = input;
    const targetFps = input.targetFps && input.targetFps > 0 ? input.targetFps : 60;

    // ---- frames: current fps vs target, plus how often it dipped ----
    const parts: { id: ScoreCategoryId; score?: number; issue?: string; facts?: string[] }[] = [];

    const fpsIsAboutTarget = input.targetIsThisDevice !== false;
    const fpsScore = fpsIsAboutTarget && input.fps !== undefined
        ? RATING_SCORE[rateFps(input.fps, targetFps)]
        : undefined;
    const stability = fpsIsAboutTarget && input.fpsSamples
        ? rateFrameStability(input.fpsSamples, targetFps) : undefined;
    const stabilityScore = stability ? RATING_SCORE[stability] : undefined;
    const frameFacts: string[] = [];
    if (fpsIsAboutTarget && input.fps !== undefined) {
        frameFacts.push(`${Math.round(input.fps)} fps against a ${targetFps} fps target`);
    }
    if (stability && input.fpsSamples?.length) {
        const dips = input.fpsSamples.filter(f => Number.isFinite(f) && f < targetFps * 0.9).length;
        frameFacts.push(`dipped below target in ${dips} of the last ${input.fpsSamples.length} seconds`);
    }
    if (!fpsIsAboutTarget) {
        frameFacts.push("not scored: fps measured here says nothing about the selected target device");
    }
    parts.push({
        id: "frames",
        facts: frameFacts,
        score: mean([fpsScore, stabilityScore]),
        issue: stability && stability !== "good"
            ? "Frame rate dips below target"
            : fpsScore !== undefined && fpsScore < 100 ? "Frame rate below target" : undefined,
    });

    // ---- gpu: VRAM against the device budget + triangles per frame ----
    const vramScore = input.gpuMemoryBytes !== undefined
        ? budgetScore(input.gpuMemoryBytes, profile.vramBudgetBytes)
        : undefined;
    const triScore = input.trianglesPerFrame !== undefined
        ? budgetScore(input.trianglesPerFrame, profile.maxTrianglesPerFrame)
        : undefined;
    // draw calls have no device budget to measure against; treat >1000 as the
    // widely-quoted "you will be CPU bound" line, and only as a soft signal
    const drawScore = input.drawCalls !== undefined ? budgetScore(input.drawCalls, 1000) : undefined;
    const vramOver = vramScore !== undefined && vramScore < 100;
    const triOver = triScore !== undefined && triScore < 100;
    const gpuFacts: string[] = [];
    if (input.trianglesPerFrame !== undefined) {
        gpuFacts.push(profile.maxTrianglesPerFrame
            ? `${formatCount(input.trianglesPerFrame)} triangles/frame, budget ${formatCount(profile.maxTrianglesPerFrame)}`
            : `${formatCount(input.trianglesPerFrame)} triangles/frame (no budget known for this device)`);
    }
    if (input.drawCalls !== undefined) gpuFacts.push(`${formatCount(input.drawCalls)} draw calls/frame, CPU-bound past ~1,000`);
    if (input.gpuMemoryBytes !== undefined) {
        gpuFacts.push(profile.vramBudgetBytes
            ? `${bytes(input.gpuMemoryBytes)} VRAM of a ${bytes(profile.vramBudgetBytes)} budget`
            : `${bytes(input.gpuMemoryBytes)} VRAM (no budget known for this device)`);
    }
    parts.push({
        id: "gpu",
        facts: gpuFacts,
        score: mean([vramScore, triScore, drawScore]),
        issue: triOver && vramOver ? "Too many triangles and too much VRAM for this device"
            : triOver ? "More triangles per frame than this device can push"
                : vramOver ? "VRAM over this device's budget"
                    : drawScore !== undefined && drawScore < 100 ? "High draw call count" : undefined,
    });

    // ---- memory: reclaimable VRAM, judged TWO ways ----
    // Against the DEVICE budget, because 200 MB thrown away is fatal on a phone and
    // irrelevant on a workstation. AND as a share of what the scene uses, because
    // "8 MB of your 12 MB is reclaimable" is free money whatever the budget says —
    // scoring that 100 (which the budget term alone did) reads as approval.
    // The budget term dominates: it can take the category to 0; the ratio term's
    // cap GROWS with the absolute amount (25 → 50 between 4 and 32 MB) — a few
    // wasted MB at a bad ratio stays a nudge, but 31 MB at 40% reclaimable is
    // real money and must not hide behind a 16 GB desktop budget.
    const waste = input.gpuMemoryWasteBytes;
    const MIN_MATERIAL_WASTE = 2 * 1024 * 1024; // below this it is rounding, not waste
    const RATIO_FREE = 0.1;  // a tenth is normal slack
    const RATIO_PENALTY_BASE = 25;
    const RATIO_PENALTY_FULL = 50;      // reached once the waste is material
    const RATIO_RAMP_START = 4 * 1024 * 1024;
    const RATIO_RAMP_END = 32 * 1024 * 1024;
    let memoryScore: number | undefined;
    if (waste !== undefined && Number.isFinite(waste)) {
        if (waste < MIN_MATERIAL_WASTE) {
            memoryScore = 100;
        }
        else {
            const budget = profile.vramBudgetBytes;
            const budgetPenalty = budget && budget > 0
                ? (waste / budget) * 250 // 40% of the budget wasted → 0
                : 0;
            const used = input.gpuMemoryBytes;
            const ratio = used && used > 0 ? waste / used : 0;
            const materiality = Math.min(1, Math.max(0, (waste - RATIO_RAMP_START) / (RATIO_RAMP_END - RATIO_RAMP_START)));
            const ratioCap = RATIO_PENALTY_BASE + (RATIO_PENALTY_FULL - RATIO_PENALTY_BASE) * materiality;
            const ratioPenalty = ratio > RATIO_FREE
                ? ((ratio - RATIO_FREE) / (1 - RATIO_FREE)) * ratioCap
                : 0;
            // with no budget known, the ratio is all we have — let it carry more
            const penalty = budget && budget > 0 ? budgetPenalty + ratioPenalty : ratio * 100;
            memoryScore = Math.max(0, Math.round(100 - penalty));
        }
    }
    const memoryFacts: string[] = [];
    if (waste !== undefined && Number.isFinite(waste)) {
        const used = input.gpuMemoryBytes;
        const share = used && used > 0 ? ` — ${sharePercent(waste / used)} of the ${bytes(used)} in use` : "";
        memoryFacts.push(`${bytes(waste)} reclaimable${share}`);
        if (profile.vramBudgetBytes) {
            memoryFacts.push(`that is ${sharePercent(waste / profile.vramBudgetBytes)} of this device's ${bytes(profile.vramBudgetBytes)} VRAM budget`);
        }
    }
    parts.push({
        id: "memory",
        facts: memoryFacts,
        score: memoryScore,
        issue: memoryScore !== undefined && memoryScore < 100 ? "Texture memory can be compressed" : undefined,
    });

    // ---- load: first uncached download, judged harder on mobile targets ----
    // A first load is judged by what a VISITOR waits for, not by what the machine
    // could technically handle: 8 MB is a lot of scene to download anywhere, and
    // scoring it perfect (the old 20 MB desktop budget did) taught the wrong thing.
    const loadBudget = profile.mobile ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    // 0 measured bytes = the probe saw NOTHING (the extension's service
    // worker attached after the page loaded) — a scene that downloaded
    // nothing does not exist, so treat it as unmeasured, never as perfect
    const transferred = input.transferredBytes !== undefined && input.transferredBytes > 0
        ? input.transferredBytes
        : undefined;
    const loadScore = transferred !== undefined
        ? budgetScore(transferred, loadBudget)
        : undefined;
    const loadFacts: string[] = [];
    if (transferred !== undefined) {
        loadFacts.push(`${bytes(transferred)} downloaded, budget ${bytes(loadBudget)} for ${profile.mobile ? "a mobile target" : "this device"}`);
    }
    parts.push({
        id: "load",
        facts: loadFacts,
        score: loadScore,
        issue: loadScore !== undefined && loadScore < 100 ? "Large first load" : undefined,
    });

    const measured = parts.filter(p => p.score !== undefined);
    const categories: ScoreCategory[] = parts.map(p => ({
        id: p.id,
        label: LABELS[p.id],
        score: p.score === undefined ? undefined : Math.round(p.score),
        rating: p.score === undefined ? undefined : ratingFor(p.score),
        measured: p.score !== undefined,
        weight: WEIGHTS[p.id],
        facts: p.facts?.length ? p.facts : undefined,
    }));

    if (measured.length === 0) {
        return { score: undefined, rating: undefined, categories, issues: [] };
    }

    // redistribute the weight of unmeasured categories over the measured ones
    const totalWeight = measured.reduce((sum, p) => sum + WEIGHTS[p.id], 0);
    const raw = measured.reduce((sum, p) => sum + p.score! * (WEIGHTS[p.id] / totalWeight), 0);
    const score = Math.max(0, Math.min(100, Math.round(raw)));

    // Attribute the loss. Each measured category lost (100 - its score) * its
    // share; rounding is absorbed by the largest issue so the sum always matches.
    const losses = measured
        .filter(p => p.issue)
        .map(p => ({
            category: p.id,
            label: p.issue!,
            exact: (100 - p.score!) * (WEIGHTS[p.id] / totalWeight),
        }))
        .sort((a, b) => b.exact - a.exact);

    const issues: ScoreIssue[] = [];
    if (losses.length > 0) {
        const target = 100 - score;
        let assigned = 0;
        for (let i = 0; i < losses.length; i++) {
            const isLast = i === losses.length - 1;
            const points = isLast ? target - assigned : Math.round(losses[i].exact);
            assigned += points;
            if (points > 0) issues.push({ category: losses[i].category, label: losses[i].label, costPoints: points });
        }
        // the remainder can make the last entry the biggest — keep worst-first true
        issues.sort((a, b) => b.costPoints - a.costPoints);
    }

    return { score, rating: ratingFor(score), categories, issues };
}
